<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { api, type AdminAnnouncement, type AdminUser, type Announcement, type Dashboard, type ProfileReview } from './lib/api'

type Route = 'home' | 'download' | 'announcements' | 'feedback' | 'privacy' | 'terms' | 'delete' | 'admin'
type ReleaseFile = { label: string; name: string; path: string; bytes: number; sha256: string; builtAt: string; note?: string }

const routes: Route[] = ['home', 'download', 'announcements', 'feedback', 'privacy', 'terms', 'delete', 'admin']
const route = ref<Route>(readHash())
const announcements = ref<Announcement[]>([
  { id: 'demo-1', title: '第三赛季「锋面」开放报名', content: '新赛季加入 5×5 与 6×6 混合赛程，首周完成三场竞速即可领取限定配色。', publishedAt: '2026-08-02', pinned: true },
  { id: 'demo-2', title: '每日挑战：反向推理', content: '今天的题面隐藏了两枚高密度运算框，适合用候选数模式拆解。', publishedAt: '2026-08-01', pinned: false },
  { id: 'demo-3', title: 'Android 内测版本 1.0.0', content: '新增自定义头像与昵称编辑，头像会同步显示在首页、好友、房间和对战资料中，并继续支持头像框。', publishedAt: '2026-08-15', pinned: true }
])
const releases = ref<ReleaseFile[]>([])
const loadedFromServer = ref(false)
const selectedCell = ref(7)
const demoNumbers = [4, 2, 5, 1, 3, 3, 5, 1, 4, 2, 2, 4, 1, 3, 5, 5, 1, 3, 2, 4, 1, 3, 4, 5, 2]
const feedback = ref({ category: '体验建议', message: '', contact: '' })
const feedbackSent = ref(false)

// —— 运营后台 ——
const adminEmail = ref('admin@calduel.local')
const adminPassword = ref('')
const adminToken = ref('')
const adminName = ref('')
const adminBusy = ref(false)
const adminNotice = ref('')
const adminError = ref('')
const dashboard = ref<Dashboard | null>(null)
const adminUsers = ref<AdminUser[]>([])
const adminAnnouncements = ref<AdminAnnouncement[]>([])
// 昵称 / 头像人工审核队列：玩家提交后不会立刻生效，必须在这里点通过。
const profileReviews = ref<ProfileReview[]>([])
// 微信首次登录的昵称 / 头像：已自动生效，留档待复核，驳回会回滚。
const autoReviews = ref<ProfileReview[]>([])
const reviewSupported = ref(true)
const rejectReasons = ref<Record<string, string>>({})
// 待审头像不是公开文件，要用令牌取回后转成 blob URL 才能预览。
const reviewAvatarUrls = ref<Record<string, string>>({})
const draft = ref({ title: '', content: '', pinned: false, published: true })
const editingId = ref('')
const editDraft = ref({ title: '', content: '', pinned: false, published: true })
// 账号搜索：违规昵称往往是很久以前的账号留下的，光看"最近 20 条"找不到。
const userQuery = ref('')
// 线上后端是否提供公告的编辑/置顶/下架/删除接口（旧版本只有 GET + POST）。
const manageSupported = ref(false)

const pageTitle = computed(() => ({
  home: '把算术变成一场对决', download: '现在就开一局', announcements: '公告与赛季',
  feedback: '把体验告诉我们', privacy: '隐私政策', terms: '用户协议', delete: '数据与账号', admin: '运营控制台'
}[route.value]))
const loggedIn = computed(() => Boolean(adminToken.value))
const publishedCount = computed(() => adminAnnouncements.value.filter((item) => item.published).length)
/**
 * 账号列表。没搜索词时只渲染最近 40 条（线上已有三百多个账号，全量渲染既慢又不实用）；
 * 输入搜索词后按昵称/邮箱过滤，最多仍只列 40 条，避免一次渲染几百行。
 */
const recentUsers = computed(() => {
  const query = userQuery.value.trim().toLowerCase()
  if (!query) return adminUsers.value.slice(0, 40)
  return adminUsers.value
    .filter((user) => (user.nickname ?? '').toLowerCase().includes(query)
      || (user.email ?? '').toLowerCase().includes(query)
      || user.id.toLowerCase().includes(query))
    .slice(0, 40)
})

function readHash(): Route {
  const value = location.hash.replace('#/', '') as Route
  return routes.includes(value) ? value : 'home'
}
function go(next: Route) {
  location.hash = `/${next}`
  route.value = next
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
function onHashChange() { route.value = readHash() }

function formatSize(bytes: number) { return `${(bytes / 1048576).toFixed(2)} MiB` }
function shortHash(hash: string) { return hash ? `${hash.slice(0, 16)}…` : '—' }
function formatDate(value: string | null) { return value ? value.slice(0, 10) : '未发布' }

async function loadAnnouncements() {
  try {
    const remote = await api.announcements()
    // 请求成功就以服务端为准（哪怕是空列表），避免线上空库时继续显示演示公告。
    announcements.value = remote
    loadedFromServer.value = true
  } catch { /* 请求失败时保留离线占位内容，页面不至于空白 */ }
}

// 安装包信息由 web/scripts/stage-artifacts.ps1 生成，避免页面里写死过期哈希。
async function loadReleases() {
  try {
    const response = await fetch('/release.json', { cache: 'no-store' })
    if (response.ok) releases.value = (await response.json()) as ReleaseFile[]
  } catch { /* 页面继续显示部署提示 */ }
}

// 安装包固定 URL 会被浏览器缓存（nginx 给了 1 小时 max-age），
// 用 SHA-256 前 8 位做查询串，保证每次重新构建后队友一定能下到新包。
function downloadUrl(file: ReleaseFile) {
  const version = (file.sha256 || '').slice(0, 8)
  return version ? `${file.path}?v=${version}` : file.path
}

async function sendFeedback() {
  if (!feedback.value.message.trim()) return
  try {
    await api.feedback({ category: feedback.value.category, content: feedback.value.message, contact: feedback.value.contact })
  } catch { /* 服务端不可用时仍然给出确认，后续可在客户端重试 */ }
  feedbackSent.value = true
  feedback.value.message = ''
}

function describe(error: unknown) {
  const message = error instanceof Error ? error.message : String(error)
  return message.includes('登录') || message.includes('401') ? '登录状态已过期，请重新登录' : message
}

async function loginAdmin() {
  adminError.value = ''
  adminNotice.value = ''
  if (!adminEmail.value.trim() || !adminPassword.value) { adminError.value = '请输入管理员邮箱与密码'; return }
  adminBusy.value = true
  try {
    const session = await api.login(adminEmail.value.trim(), adminPassword.value)
    if (session.user.role !== 'ADMIN') { adminError.value = '该账号不是管理员'; return }
    adminToken.value = session.accessToken
    adminName.value = session.user.nickname || session.user.email
    adminPassword.value = ''
    await refreshAdmin()
    await loadAnnouncements()
  } catch (error) {
    adminError.value = `登录失败：${describe(error)}`
  } finally {
    adminBusy.value = false
  }
}

async function refreshAdmin() {
  adminBusy.value = true
  adminError.value = ''
  try {
    const [stats, users, list] = await Promise.all([
      api.dashboard(adminToken.value),
      api.users(adminToken.value),
      api.adminAnnouncements(adminToken.value)
    ])
    dashboard.value = stats
    adminUsers.value = users
    adminAnnouncements.value = list
    adminNotice.value = `已连接运营接口 · ${new Date().toLocaleTimeString('zh-CN')}`
    await probeManageSupport()
    await loadProfileReviews()
  } catch (error) {
    adminError.value = describe(error)
    if (adminError.value.includes('重新登录')) logout()
  } finally {
    adminBusy.value = false
  }
}

/**
 * 拉取待审昵称 / 头像。
 * 线上旧后端没有这组接口，返回 401/404 时把区块标成「后端待升级」，
 * 而不是让整个控制台报错。
 */
async function loadProfileReviews() {
  releaseReviewAvatarUrls()
  let pending: ProfileReview[] = []
  let auto: ProfileReview[] = []
  try {
    pending = await api.profileReviews(adminToken.value, 'PENDING')
    reviewSupported.value = true
  } catch {
    profileReviews.value = []
    autoReviews.value = []
    reviewSupported.value = false
    return
  }
  // 微信平台身份的留档记录：接口不存在或出错都不该影响待审队列的展示。
  try { auto = await api.profileReviews(adminToken.value, 'AUTO') } catch { auto = [] }
  profileReviews.value = pending
  autoReviews.value = auto
  for (const item of [...pending, ...auto]) {
    if (item.kind !== 'AVATAR') continue
    if (item.pendingAvatarUrl) { reviewAvatarUrls.value[item.id] = item.pendingAvatarUrl; continue }
    try {
      reviewAvatarUrls.value[item.id] = await api.profileReviewAvatar(adminToken.value, item.id)
    } catch { /* 预览失败不影响审核本身 */ }
  }
}

/** blob URL 必须显式回收，否则反复刷新会一直占内存。 */
function releaseReviewAvatarUrls() {
  for (const url of Object.values(reviewAvatarUrls.value)) {
    if (url.startsWith('blob:')) URL.revokeObjectURL(url)
  }
  reviewAvatarUrls.value = {}
}

async function decideProfileReview(item: ProfileReview, status: 'APPROVED' | 'REJECTED') {
  adminBusy.value = true
  adminError.value = ''
  try {
    const reason = status === 'REJECTED' ? (rejectReasons.value[item.id] ?? '') : ''
    await api.reviewProfile(adminToken.value, item.id, status, reason)
    const label = item.kind === 'NICKNAME' ? '昵称' : '头像'
    if (status === 'APPROVED') {
      adminNotice.value = item.status === 'AUTO'
        ? `${label}已确认无误`
        : `${label}已通过，玩家下次刷新即可看到`
    } else {
      adminNotice.value = item.status === 'AUTO'
        ? `已驳回，该账号的${label}已回滚`
        : `已驳回，玩家的旧${label}保持不变`
    }
    delete rejectReasons.value[item.id]
    await loadProfileReviews()
  } catch (error) {
    adminError.value = `审核失败：${describe(error)}`
  } finally {
    adminBusy.value = false
  }
}

async function approveAllProfileReviews() {
  if (!profileReviews.value.length) return
  if (!window.confirm(`把当前 ${profileReviews.value.length} 条待审内容全部通过？`)) return
  adminBusy.value = true
  try {
    const result = await api.approveAllProfileReviews(adminToken.value)
    adminNotice.value = `已批量通过 ${result.approved} 条`
    await loadProfileReviews()
  } catch (error) {
    adminError.value = `批量通过失败：${describe(error)}`
  } finally {
    adminBusy.value = false
  }
}

/**
 * 重置违规资料。这是破坏性操作（换昵称 + 删头像），所以必须二次确认，
 * 并且把被清掉的原值回显出来，避免误点把正常玩家清掉。
 */
async function resetUserProfile(user: AdminUser) {
  const label = user.nickname || user.id.slice(0, 8)
  if (!window.confirm(
    `把「${label}」的昵称改成中性占位、清掉自定义头像？\n\n该操作会留档，但玩家资料无法还原。`
  )) return
  const reason = window.prompt('重置原因（会记入留档，可留空）', '违规昵称/头像') ?? ''
  adminBusy.value = true
  adminError.value = ''
  try {
    const result = await api.resetUserProfile(adminToken.value, user.id, reason)
    adminNotice.value = `已重置「${label}」，新昵称为 ${result.nickname}`
    await refreshAdmin()
  } catch (error) {
    adminError.value = `重置失败：${describe(error)}`
  } finally {
    adminBusy.value = false
  }
}

/**
 * 探测后端是否支持公告的编辑/下架/删除。
 * 线上旧版后端只有 GET + POST；对未映射的方法，安全层返回的是 401 AUTH_REQUIRED
 * 而不是 405，所以「返回 401」= 接口不存在（此时也才说明真的该重新登录，
 * 但登录过期会先在 dashboard 请求上暴露，不会走到这里）。
 */
async function probeManageSupport() {
  try {
    const response = await fetch('/api/v1/admin/announcements/__probe__', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${adminToken.value}` },
      body: JSON.stringify({ pinned: false })
    })
    manageSupported.value = response.status !== 401
  } catch {
    manageSupported.value = false
  }
}

async function publishAnnouncement() {
  if (!draft.value.title.trim() || !draft.value.content.trim()) { adminError.value = '标题和内容都不能为空'; return }
  adminBusy.value = true
  adminError.value = ''
  try {
    await api.createAnnouncement(adminToken.value, { ...draft.value, title: draft.value.title.trim(), content: draft.value.content.trim() })
    draft.value = { title: '', content: '', pinned: false, published: true }
    adminNotice.value = '公告已发布，三端与官网同步可见'
    await refreshAdmin()
    await loadAnnouncements()
  } catch (error) {
    adminError.value = `发布失败：${describe(error)}`
  } finally {
    adminBusy.value = false
  }
}

function startEdit(item: AdminAnnouncement) {
  editingId.value = item.id
  editDraft.value = { title: item.title, content: item.content, pinned: item.pinned, published: item.published }
}
function cancelEdit() { editingId.value = '' }

async function saveEdit() {
  if (!editDraft.value.title.trim() || !editDraft.value.content.trim()) { adminError.value = '标题和内容都不能为空'; return }
  adminBusy.value = true
  try {
    await api.updateAnnouncement(adminToken.value, editingId.value, {
      title: editDraft.value.title.trim(), content: editDraft.value.content.trim(),
      pinned: editDraft.value.pinned, published: editDraft.value.published
    })
    editingId.value = ''
    adminNotice.value = '公告已更新'
    await refreshAdmin()
    await loadAnnouncements()
  } catch (error) {
    adminError.value = `保存失败：${describe(error)}`
  } finally {
    adminBusy.value = false
  }
}

async function togglePinned(item: AdminAnnouncement) {
  try {
    await api.updateAnnouncement(adminToken.value, item.id, { pinned: !item.pinned })
    await refreshAdmin()
    await loadAnnouncements()
  } catch (error) { adminError.value = describe(error) }
}

async function togglePublished(item: AdminAnnouncement) {
  try {
    await api.updateAnnouncement(adminToken.value, item.id, { published: !item.published })
    adminNotice.value = item.published ? '公告已下架' : '公告已上架'
    await refreshAdmin()
    await loadAnnouncements()
  } catch (error) { adminError.value = describe(error) }
}

async function removeAnnouncement(item: AdminAnnouncement) {
  if (!window.confirm(`彻底删除公告「${item.title}」？该操作不可撤销。`)) return
  try {
    await api.deleteAnnouncement(adminToken.value, item.id)
    adminNotice.value = '公告已删除'
    await refreshAdmin()
    await loadAnnouncements()
  } catch (error) { adminError.value = describe(error) }
}

function logout() {
  adminToken.value = ''
  adminPassword.value = ''
  adminNotice.value = ''
  adminError.value = ''
  dashboard.value = null
  adminUsers.value = []
  adminAnnouncements.value = []
  releaseReviewAvatarUrls()
  profileReviews.value = []
  autoReviews.value = []
  rejectReasons.value = {}
  editingId.value = ''
}

onMounted(() => {
  window.addEventListener('hashchange', onHashChange)
  window.scrollTo(0, 0)
  loadAnnouncements()
  loadReleases()
})
</script>

<template>
  <div class="site-shell">
    <header class="topbar">
      <button class="brand" aria-label="返回首页" @click="go('home')"><span class="brand-mark">cd</span><span>CALC<span class="brand-accent">DUEL</span></span></button>
      <nav class="nav-links" aria-label="主导航"><button :class="{ active: route === 'home' }" @click="go('home')">产品</button><button :class="{ active: route === 'download' }" @click="go('download')">下载</button><button :class="{ active: route === 'announcements' }" @click="go('announcements')">公告</button><button :class="{ active: route === 'feedback' }" @click="go('feedback')">反馈</button></nav>
      <button class="nav-cta" @click="go('download')">获取 Android <span>↗</span></button>
    </header>
    <main>
      <section v-if="route === 'home'" class="hero page-width">
        <div class="hero-copy reveal"><div class="eyebrow"><span class="live-dot"></span> Android 内测版 · 1.0.0 / 域名联调中</div><h1>{{ pageTitle }}<em>。</em></h1><p class="lede">在每个格子里做出一个决定。算独博弈把 KenKen 的逻辑密度、竞速的心跳和一张真正好用的棋盘，压进掌心。</p><div class="hero-actions"><button class="button button-dark" @click="go('download')">下载内测 APK <span>↓</span></button><button class="text-button" @click="go('announcements')">查看赛季日志 <span>→</span></button></div><dl class="mini-stats"><div><dt>4—7</dt><dd>棋盘尺寸</dd></div><div><dt>4</dt><dd>难度档位</dd></div><div><dt>∞</dt><dd>可复现题局</dd></div></dl></div>
        <div class="hero-board-wrap reveal delay-1"><div class="board-meta"><span>DAILY / 08.02</span><span class="meta-time">00:47.21</span></div><div class="puzzle-board"><button v-for="(number, index) in demoNumbers" :key="index" class="puzzle-cell" :class="{ selected: index === selectedCell, related: Math.floor(index / 5) === Math.floor(selectedCell / 5) || index % 5 === selectedCell % 5, cage: [0, 1, 5, 6, 7, 10, 11, 12, 13, 14, 18, 19, 20, 23, 24].includes(index) }" @click="selectedCell = index"><span v-if="index === selectedCell" class="cell-target">12×</span><strong>{{ number }}</strong></button></div><div class="board-foot"><span><i class="legend-dot teal"></i> 当前格</span><span><i class="legend-dot yellow"></i> 运算框 12×</span><span>1 / 3 提示</span></div></div>
      </section>

      <section v-else class="subpage page-width">
        <div class="subpage-heading"><div class="eyebrow">CALCDUEL / {{ route.toUpperCase() }}</div><h1>{{ pageTitle }}<em>。</em></h1></div>

        <div v-if="route === 'download'" class="download-grid">
          <div class="download-card dark-panel">
            <div class="panel-kicker">CALCDUEL / 内测包</div>
            <h2>一张棋盘，三种节奏。</h2>
            <p>游客即可开始。离线题局、每日挑战和实时竞速使用同一套题目引擎，内测服务器通过 HTTPS / WSS 联调。</p>
            <div v-if="releases.length" class="release-list">
              <div v-for="file in releases" :key="file.path" class="release-row">
                <div>
                  <strong>{{ file.label }}</strong>
                  <small>{{ file.name }} · {{ file.builtAt }}</small>
                  <small>{{ formatSize(file.bytes) }} · SHA-256 {{ shortHash(file.sha256) }}</small>
                  <small v-if="file.note">{{ file.note }}</small>
                </div>
                <a class="download-link" :href="downloadUrl(file)" :download="file.name" :aria-label="`下载 ${file.label}`">
                  {{ file.name.toLowerCase().endsWith('.hap') ? '下载 HAP' : '下载 APK' }}
                  <span aria-hidden="true">↓</span>
                </a>
              </div>
            </div>
            <p v-else class="release-pending">安装包信息未生成，请先运行 web/scripts/stage-artifacts.ps1 后再构建。</p>
          </div>
          <div class="install-steps">
            <div><b>01</b><span><strong>下载并安装</strong><small>Android 8.0+ / minSdk 26</small></span></div>
            <div><b>02</b><span><strong>游客模式直接开始</strong><small>无需注册，可随时转为正式账号</small></span></div>
            <div><b>03</b><span><strong>连接内测服务器</strong><small>calcduel.site · HTTPS / WSS</small></span></div>
          </div>
        </div>

        <div v-else-if="route === 'announcements'" class="announcement-layout">
          <div class="announcement-list">
            <p v-if="loadedFromServer && !announcements.length" class="admin-empty">还没有发布公告。运营后台发布后，这里会与三端同步显示。</p>
            <article v-for="item in announcements" :key="item.id" class="announcement-row">
              <div class="announcement-date">{{ formatDate(item.publishedAt) }}<span v-if="item.pinned">PINNED</span></div>
              <div><h2>{{ item.title }}</h2><p>{{ item.content }}</p></div>
              <span class="row-arrow">↗</span>
            </article>
          </div>
          <aside class="side-note"><span class="note-index">03</span><h3>赛季日志</h3><p>每一局都留下可复盘的轨迹。我们会在这里记录版本、规则和新的棋盘。</p></aside>
        </div>

        <div v-else-if="route === 'feedback'" class="form-layout">
          <div class="form-intro"><span class="note-index">HELLO</span><h2>哪里卡住了，或者哪里刚刚好？</h2><p>一条具体的反馈，能让下一局更顺。我们会在 48 小时内查看。</p></div>
          <form class="feedback-form" @submit.prevent="sendFeedback">
            <label>反馈类型<select v-model="feedback.category"><option>体验建议</option><option>问题报告</option><option>题目反馈</option><option>数据请求</option></select></label>
            <label>内容<textarea v-model="feedback.message" required rows="5" placeholder="描述你的局面、设备和期望结果"></textarea></label>
            <label>联系方式 <span class="optional">可选</span><input v-model="feedback.contact" placeholder="邮箱或 CalcDuel ID" /></label>
            <button class="button button-dark" type="submit">发送反馈 <span>→</span></button>
            <p v-if="feedbackSent" class="form-success">已收到。谢谢你把这一局交给我们。</p>
          </form>
        </div>

        <div v-else-if="route === 'admin'" class="admin-shell">
          <div v-if="!loggedIn" class="admin-login dark-panel">
            <div class="panel-kicker">STAFF ONLY / ROLE GUARD</div>
            <h2>进入运营控制台</h2>
            <p>使用管理员账号登录，令牌只保存在当前页面内存中，不会写入本地存储。后台只允许 ADMIN 角色访问。</p>
            <label>管理员邮箱<input v-model="adminEmail" type="email" autocomplete="username" placeholder="admin@calduel.local" /></label>
            <label>密码<input v-model="adminPassword" type="password" autocomplete="current-password" placeholder="••••••••" @keyup.enter="loginAdmin" /></label>
            <button class="button button-light" :disabled="adminBusy" @click="loginAdmin">{{ adminBusy ? '验证中…' : '登录控制台' }} <span>→</span></button>
            <small v-if="adminError" class="admin-error">{{ adminError }}</small>
          </div>

          <template v-else>
            <div class="admin-top">
              <div><div class="eyebrow">运营概览 / {{ adminName }}</div><h2>今天的棋盘正在发生什么</h2></div>
              <div class="admin-actions">
                <button class="text-button" :disabled="adminBusy" @click="refreshAdmin">刷新 <span>↻</span></button>
                <button class="text-button" @click="logout">退出控制台 <span>↗</span></button>
              </div>
            </div>

            <div v-if="dashboard" class="metric-grid">
              <div><span>注册档案</span><strong>{{ dashboard.users.toLocaleString() }}</strong><small>累计账号</small></div>
              <div><span>题库规模</span><strong>{{ dashboard.puzzles.toLocaleString() }}</strong><small>唯一解校验 100%</small></div>
              <div><span>累计对局</span><strong>{{ dashboard.scores }}</strong><small>含 {{ dashboard.pendingScores }} 条待审核成绩</small></div>
              <div><span>待处理反馈</span><strong>{{ dashboard.feedbackOpen }}</strong><small class="coral-text">需要今天处理</small></div>
            </div>

            <p v-if="adminNotice" class="admin-notice">{{ adminNotice }}</p>
            <p v-if="adminError" class="admin-error">{{ adminError }}</p>
            <p v-if="!manageSupported" class="admin-warn">
              当前线上后端只提供「发布 + 查看」公告接口，编辑 / 置顶 / 下架 / 删除需要升级后端（本地 server 源码已包含这些接口）。
            </p>

            <div class="admin-columns">
              <section class="admin-table">
                <div class="table-heading"><h3>公告管理</h3><span>{{ adminAnnouncements.length }} 条 · {{ publishedCount }} 条已上架</span></div>
                <div v-if="!adminAnnouncements.length" class="admin-empty">还没有公告。右侧写一条，发布后三端与官网同步可见。</div>
                <article v-for="item in adminAnnouncements" :key="item.id" class="admin-row">
                  <template v-if="editingId === item.id">
                    <input v-model="editDraft.title" placeholder="公告标题" />
                    <textarea v-model="editDraft.content" rows="4" placeholder="公告内容"></textarea>
                    <div class="admin-row-foot">
                      <label class="switch"><input type="checkbox" v-model="editDraft.published" /> 已上架</label>
                      <label class="switch"><input type="checkbox" v-model="editDraft.pinned" /> 置顶</label>
                      <div class="admin-row-actions">
                        <button class="chip chip-solid" :disabled="adminBusy" @click="saveEdit">保存</button>
                        <button class="chip" @click="cancelEdit">取消</button>
                      </div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="admin-row-head">
                      <strong>{{ item.title }}</strong>
                      <span class="tag" :class="item.published ? 'tag-on' : 'tag-off'">{{ item.published ? '已上架' : '已下架' }}</span>
                      <span v-if="item.pinned" class="tag tag-pin">置顶</span>
                      <span class="admin-row-date">{{ formatDate(item.publishedAt) }}</span>
                    </div>
                    <p class="admin-row-body">{{ item.content }}</p>
                    <div v-if="manageSupported" class="admin-row-actions">
                      <button class="chip" @click="startEdit(item)">编辑</button>
                      <button class="chip" @click="togglePinned(item)">{{ item.pinned ? '取消置顶' : '置顶' }}</button>
                      <button class="chip" @click="togglePublished(item)">{{ item.published ? '下架' : '上架' }}</button>
                      <button class="chip chip-danger" @click="removeAnnouncement(item)">删除</button>
                    </div>
                  </template>
                </article>
              </section>

              <section class="publish-box">
                <div class="table-heading"><h3>发布公告</h3><span>发布后三端与官网同步</span></div>
                <input v-model="draft.title" placeholder="公告标题" maxlength="150" />
                <textarea v-model="draft.content" rows="6" placeholder="这次要告诉玩家什么？"></textarea>
                <label class="switch"><input type="checkbox" v-model="draft.published" /> 立即上架</label>
                <label v-if="manageSupported" class="switch"><input type="checkbox" v-model="draft.pinned" /> 置顶显示</label>
                <button class="button button-dark" :disabled="adminBusy" @click="publishAnnouncement">发布到三端 <span>↗</span></button>
              </section>
            </div>

            <section class="admin-table admin-users">
              <div class="table-heading">
                <h3>账号与违规资料清理</h3>
                <span>共 {{ adminUsers.length }} 条 · 命中 {{ recentUsers.length }} 条</span>
              </div>
              <input v-model="userQuery" class="user-search" placeholder="按昵称 / 邮箱搜索（找旧账号就靠这个）" />
              <p class="admin-hint">
                「重置资料」会把昵称换成中性占位、清掉自定义头像，并作废该账号的待审申请，操作会留档。
                已经生效的违规昵称/头像没有待审记录，只能在这里清。
              </p>
              <table>
                <thead><tr><th>昵称</th><th>身份</th><th>头像</th><th>加入时间</th><th></th></tr></thead>
                <tbody>
                  <tr v-for="user in recentUsers" :key="user.id">
                    <td>{{ user.nickname }}</td>
                    <td><span class="role-tag">{{ user.role }}</span></td>
                    <td><span class="tag" :class="user.customAvatar ? 'tag-on' : 'tag-off'">{{ user.customAvatar ? '自定义' : '几何' }}</span></td>
                    <td>{{ formatDate(user.createdAt) }}</td>
                    <td>
                      <button class="chip chip-danger" :disabled="adminBusy" @click="resetUserProfile(user)">重置资料</button>
                    </td>
                  </tr>
                  <tr v-if="!recentUsers.length"><td colspan="5" class="admin-empty">没有匹配的账号。</td></tr>
                </tbody>
              </table>
            </section>

            <section class="admin-table admin-reviews">
              <div class="table-heading">
                <h3>昵称与头像审核</h3>
                <span>
                  {{ profileReviews.length }} 条待审 ·
                  {{ dashboard?.pendingProfileReviews ?? profileReviews.length }} 条累计
                </span>
                <button v-if="reviewSupported && profileReviews.length" class="chip" :disabled="adminBusy" @click="approveAllProfileReviews">
                  全部通过
                </button>
              </div>
              <p v-if="!reviewSupported" class="admin-warn">
                当前线上后端还没有审核接口。昵称与头像现在是「提交即生效」，需要先升级后端才能改成人工过审。
              </p>
              <p v-else-if="!profileReviews.length" class="admin-empty">
                队列是空的。玩家改名或换头像后会先出现在这里，点「通过」才对外生效。
              </p>
              <article v-for="item in profileReviews" :key="item.id" class="admin-row review-row">
                <div class="admin-row-head">
                  <strong>{{ item.kind === 'NICKNAME' ? '改名申请' : '头像申请' }}</strong>
                  <span class="tag tag-off">{{ item.userNickname || item.userEmail || item.userId.slice(0, 8) }}</span>
                  <span class="admin-row-date">{{ formatDate(item.createdAt) }}</span>
                </div>
                <div class="review-body">
                  <img v-if="item.kind === 'AVATAR' && reviewAvatarUrls[item.id]" class="review-avatar"
                       :src="reviewAvatarUrls[item.id]" alt="待审头像" />
                  <div class="review-values">
                    <p v-if="item.kind === 'NICKNAME'" class="review-value">
                      申请昵称：<strong>{{ item.nickname }}</strong>
                      <small>（当前显示：{{ item.previousNickname }}）</small>
                    </p>
                    <p v-else class="review-value">
                      新头像预览<small>（当前头像：{{ item.previousAvatarUrl || '几何头像' }}）</small>
                    </p>
                  </div>
                </div>
                <div class="admin-row-actions">
                  <button class="chip chip-solid" :disabled="adminBusy" @click="decideProfileReview(item, 'APPROVED')">通过</button>
                  <input v-model="rejectReasons[item.id]" class="review-reason" placeholder="驳回理由（可留空）" maxlength="200" />
                  <button class="chip chip-danger" :disabled="adminBusy" @click="decideProfileReview(item, 'REJECTED')">驳回</button>
                </div>
              </article>

              <template v-if="autoReviews.length">
                <div class="table-heading review-subheading">
                  <h4>微信身份 · 已自动生效</h4>
                  <span>微信首次登录的昵称与头像直接生效，驳回会回滚成占位昵称 / 无自定义头像</span>
                </div>
                <article v-for="item in autoReviews" :key="item.id" class="admin-row review-row review-row-auto">
                  <div class="admin-row-head">
                    <strong>{{ item.kind === 'NICKNAME' ? '微信昵称' : '微信头像' }}</strong>
                    <span class="tag tag-on">已生效</span>
                    <span class="tag tag-off">{{ item.userNickname || item.userEmail || item.userId.slice(0, 8) }}</span>
                    <span class="admin-row-date">{{ formatDate(item.createdAt) }}</span>
                  </div>
                  <div class="review-body">
                    <img v-if="item.kind === 'AVATAR' && reviewAvatarUrls[item.id]" class="review-avatar"
                         :src="reviewAvatarUrls[item.id]" alt="微信头像" />
                    <div class="review-values">
                      <p v-if="item.kind === 'NICKNAME'" class="review-value">
                        微信昵称：<strong>{{ item.nickname }}</strong>
                        <small>（登录前：{{ item.previousNickname }}）</small>
                      </p>
                      <p v-else class="review-value">
                        微信头像<small>（登录前：{{ item.previousAvatarUrl || '几何头像' }}）</small>
                      </p>
                    </div>
                  </div>
                  <div class="admin-row-actions">
                    <button class="chip" :disabled="adminBusy" @click="decideProfileReview(item, 'APPROVED')">确认无误</button>
                    <input v-model="rejectReasons[item.id]" class="review-reason" placeholder="驳回理由（可留空）" maxlength="200" />
                    <button class="chip chip-danger" :disabled="adminBusy" @click="decideProfileReview(item, 'REJECTED')">驳回回滚</button>
                  </div>
                </article>
              </template>
            </section>
          </template>
        </div>

        <div v-else class="legal-copy">
          <p class="legal-lede">CalcDuel 以最少的数据，支撑一场完整的对局。</p>
          <h2>{{ route === 'privacy' ? '我们保存什么' : route === 'terms' ? '如何使用服务' : '导出与删除' }}</h2>
          <p>游客档案只保存随机标识、题局进度和必要的设备状态。注册账号会增加邮箱与加密密码摘要。数据用于同步存档、排行榜与反作弊校验，不出售给第三方。</p>
          <h2>{{ route === 'delete' ? '现在开始处理' : '你的选择' }}</h2>
          <p>你可以在 Android 的“设置 → 数据与账号”导出 JSON 或发起删除请求。删除会清除进度、好友、成就及运营记录，并在备份周期内完成物理删除。</p>
          <button class="button button-dark" @click="go('feedback')">提交数据请求 <span>→</span></button>
        </div>
      </section>
    </main>
    <footer class="footer page-width"><div class="footer-brand"><span class="brand-mark">cd</span><div><strong>CALCDUEL</strong><small>把每一步算清楚。</small></div></div><div class="footer-links"><button @click="go('announcements')">公告</button><button @click="go('feedback')">反馈</button><button @click="go('privacy')">隐私</button><button @click="go('terms')">协议</button><button @click="go('delete')">数据删除</button><button @click="go('admin')">后台</button></div><span class="footer-copy">© 2026 CalcDuel · 桂林电子科技大学</span></footer>
  </div>
</template>
